import { useQueries } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { trpc } from "lib/trpc"
import { useMemo } from "react"
import { ClientSwitcher } from "@/components/client-header"
import { Page } from "@/components/page"
import {
  type ClientEstateListItem,
  ClientEstatesList,
} from "@/components/professional/clientEstatesList"
import { Typography } from "@/components/ui/typography"

export const Route = createFileRoute("/_authed/_professional/professional/clients")({
  component: RouteComponent,
})

const MOCK_READINESS_SCORES = [62, 32, 100] as const

function RouteComponent() {
  return <ClientsRouteContent />
}

export function ClientsRouteContent() {
  const {
    data: estates = [],
    isLoading: isLoadingEstates,
    error: estatesError,
  } = trpc.estates.list.useQuery()

  const trpcContext = trpc.useContext()

  const estateQueries = useQueries({
    queries: estates.map((estate) => ({
      queryKey: ["estates", "getById", { estateId: estate.id }],
      queryFn: () =>
        trpcContext.estates.getById.fetch({
          estateId: estate.id,
        }),
      enabled: Boolean(estate.id),
    })),
  })

  const contactQueries = useQueries({
    queries: estates.map((estate) => ({
      queryKey: ["contacts", "list", { estateId: estate.id }],
      queryFn: () =>
        trpcContext.contacts.list.fetch({
          estateId: estate.id,
        }),
      enabled: Boolean(estate.id),
    })),
  })

  const clientEstates = useMemo<ClientEstateListItem[]>(() => {
    return estates.map((estate, index) => {
      const estateDetails = estateQueries[index]?.data
      const contacts = contactQueries[index]?.data ?? []

      const testator = contacts.find((contact) => contact.id === estateDetails?.testatorContactId)

      return {
        id: estate.id,
        firstName: testator?.firstName ?? null,
        lastName: testator?.lastName ?? null,
        email: testator?.email ?? null,
        createdAt: null,
        updatedAt: null,

        // Temporary test data — remove when the API provides scores.
        readinessScore: MOCK_READINESS_SCORES[index] ?? null,
      }
    })
  }, [estates, estateQueries, contactQueries])

  const isLoadingEstateDetails = estateQueries.some((query) => query.isLoading)
  const isLoadingContacts = contactQueries.some((query) => query.isLoading)
  const isLoading = isLoadingEstates || isLoadingEstateDetails || isLoadingContacts
  const estateDetailsError = estateQueries.find((query) => query.error)?.error
  const contactsError = contactQueries.find((query) => query.error)?.error
  const error = estatesError ?? estateDetailsError ?? contactsError

  return (
    <Page
      headerSlot={
        <div className="flex w-full items-center justify-between gap-6">
          <Typography as="h1" size="2xl">
            Clients
          </Typography>

          <div className="shrink-0 lg:w-full lg:max-w-md">
            <ClientSwitcher />
          </div>
        </div>
      }
    >
      {error && (
        <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="font-medium text-red-800">Unable to load clients</p>
          <p className="text-sm text-red-700">{error.message}</p>
        </div>
      )}

      <ClientEstatesList clients={clientEstates} isLoading={isLoading} />
    </Page>
  )
}
